// Shared admin helpers: money input, message banner, section scaffolding.
import React, { useCallback, useState } from "react";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { Button, Card, Checkbox, Field, Input, Modal, Notice, Select, Textarea } from "../ui";
import { ApiError } from "../../lib/api";

export function MoneyInput({
  label,
  paise,
  onChangePaise,
  hint,
  id,
}: {
  label: string;
  paise: number | null | undefined;
  onChangePaise: (paise: number | null) => void;
  hint?: string;
  id?: string;
}) {
  const [text, setText] = useState(
    paise === null || paise === undefined ? "" : String(paise / 100),
  );
  React.useEffect(() => {
    setText(paise === null || paise === undefined ? "" : String(paise / 100));
  }, [paise]);
  return (
    <Field label={label} hint={hint} htmlFor={id}>
      <Input
        id={id}
        type="number"
        min={0}
        step="0.01"
        inputMode="decimal"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          const v = e.target.value.trim();
          if (v === "") {
            onChangePaise(null);
            return;
          }
          const num = Number(v);
          onChangePaise(Number.isFinite(num) && num >= 0 ? Math.round(num * 100) : null);
        }}
      />
    </Field>
  );
}

export function useAdminAction() {
  const [message, setMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const run = useCallback(
    async (fn: () => Promise<void>, successText = "Saved.") => {
      setBusy(true);
      setMessage(null);
      try {
        await fn();
        setMessage({ tone: "success", text: successText });
      } catch (err) {
        const text =
          err instanceof ApiError ? err.message : "Something went wrong. Please try again.";
        setMessage({ tone: "error", text });
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  return { message, busy, run, setMessage };
}

export function ActionMessage({ message }: { message: { tone: "success" | "error"; text: string } | null }) {
  if (!message) return null;
  return (
    <div
      className={`mt-3 flex items-start gap-2 rounded-xl border px-4 py-2.5 text-sm ${
        message.tone === "success"
          ? "border-emerald-400/30 bg-emerald-500/100/10 text-emerald-200"
          : "border-red-400/30 bg-red-500/100/10 text-red-200"
      }`}
      role="status"
    >
      {message.tone === "success" ? (
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      ) : (
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      )}
      {message.text}
    </div>
  );
}

export function PanelSection({
  title,
  description,
  children,
  action,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <Card className="p-6 md:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-white">{title}</h2>
          {description && <p className="mt-1 max-w-2xl text-sm text-wandor-muted">{description}</p>}
        </div>
        {action}
      </div>
      <div className="mt-6">{children}</div>
    </Card>
  );
}

export function RowActions({
  onEdit,
  onDelete,
  deleteLabel = "Delete",
  extra,
}: {
  onEdit?: () => void;
  onDelete?: () => void;
  deleteLabel?: string;
  extra?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-end gap-2">
      {extra}
      {onEdit && (
        <Button size="sm" variant="secondary" onClick={onEdit}>
          Edit
        </Button>
      )}
      {onDelete && (
        <Button size="sm" variant="danger" onClick={onDelete}>
          {deleteLabel}
        </Button>
      )}
    </div>
  );
}

export { Field, Input, Select, Textarea, Button, Card, Checkbox, Modal, Notice };
